/**
 * Single frontend source of truth for legal document paths and absolute URLs.
 * Mirrors backend LegalUrlResolver — slug is identity; paths are derived.
 */

import { LOCAL_BACKEND_URL } from '@/lib/env';

export const LEGAL_PUBLIC_ORIGIN =
  (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_DASHBOARD_URL
    ? process.env.NEXT_PUBLIC_DASHBOARD_URL.replace(/\/$/, '')
    : null) || 'http://127.0.0.1:3000';

export type LegalPathSource = {
  slug?: string | null;
  public_path?: string | null;
  resolved_public_path?: string | null;
  custom_public_path?: string | null;
};

export const LEGACY_SHORT_REDIRECTS: Record<string, string> = {
  '/terms': 'terms-and-conditions',
  '/privacy': 'privacy-policy',
  '/cookie-policy': 'cookie-policy',
  '/refund': 'refund-and-cancellation-policy',
  '/code-of-conduct': 'code-of-conduct',
};

export const LEGACY_SLUG_ALIASES: Record<string, string> = {
  privacy: 'privacy-policy',
  terms: 'terms-and-conditions',
  'terms-of-service': 'terms-and-conditions',
  'terms-conditions': 'terms-and-conditions',
  refund: 'refund-and-cancellation-policy',
  'refund-policy': 'refund-and-cancellation-policy',
  'refund-cancellation-policy': 'refund-and-cancellation-policy',
  cookie: 'cookie-policy',
  cookies: 'cookie-policy',
};

export function slugify(input: string): string {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'document';
}

export function canonicalLegalPath(slug: string): string {
  return `/legal/${slugify(slug)}`;
}

export function normalizeLegalPath(input: string, fallbackSlug?: string | null): string {
  let path = String(input || '').trim();
  if (!path) {
    return fallbackSlug ? canonicalLegalPath(fallbackSlug) : '/';
  }

  if (/^\/+https?:\/\//i.test(path)) {
    path = path.replace(/^\/+(https?):\/\//i, '$1://');
  }

  if (path.startsWith('//') && !path.startsWith('///')) {
    path = `https:${path}`;
  }

  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname || '/';
    } catch {
      path = '/';
    }
  }

  try {
    path = decodeURIComponent(path);
  } catch {
    /* keep */
  }

  if (path.includes('..') || path.includes('\0')) {
    return fallbackSlug ? canonicalLegalPath(fallbackSlug) : '/';
  }

  if (!path.startsWith('/')) path = `/${path}`;
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');

  const legalMatch = path.match(/^\/legal\/(.+)$/i);
  if (legalMatch?.[1]) {
    path = `/legal/${slugify(legalMatch[1])}`;
  }

  path = path.toLowerCase();

  if (path === '/' && fallbackSlug) {
    return canonicalLegalPath(fallbackSlug);
  }

  return path;
}

export function isValidPublicPath(path: string): boolean {
  const normalized = normalizeLegalPath(path);
  if (!normalized || normalized === '/') return false;
  if (/^\/?https?:/i.test(normalized)) return false;
  if (LEGACY_SHORT_REDIRECTS[normalized]) return true;
  return /^\/legal\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized);
}

/** Resolve public path: custom override → computed /legal/{slug}. API public_path is already computed. */
export function legalPath(source: LegalPathSource | string | null | undefined): string {
  if (!source) return '/legal';
  if (typeof source === 'string') {
    const n = normalizeLegalPath(source);
    return isValidPublicPath(n) ? n : canonicalLegalPath(source);
  }

  const slug = source.slug || '';
  if (source.custom_public_path) {
    const custom = normalizeLegalPath(source.custom_public_path, slug);
    if (isValidPublicPath(custom) && custom !== canonicalLegalPath(slug)) {
      return custom;
    }
  }

  const raw = source.resolved_public_path || source.public_path || '';
  if (raw) {
    const normalized = normalizeLegalPath(raw, slug);
    if (isValidPublicPath(normalized)) return normalized;
  }

  return slug ? canonicalLegalPath(slug) : '/legal';
}

export function legalAbsoluteUrl(
  pathOrDoc: string | LegalPathSource | null | undefined,
  origin: string = LEGAL_PUBLIC_ORIGIN,
): string {
  const base = (origin || LEGAL_PUBLIC_ORIGIN).replace(/\/$/, '');
  if (!pathOrDoc) return `${base}/legal`;

  if (typeof pathOrDoc === 'string') {
    const trimmed = pathOrDoc.trim();
    if (/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('/')) {
      const path = normalizeLegalPath(trimmed);
      return isValidPublicPath(path) ? `${base}${path}` : trimmed;
    }
    const path = legalPath(trimmed.startsWith('/') || trimmed.includes('/') ? trimmed : { slug: trimmed });
    return `${base}${path}`;
  }

  return `${base}${legalPath(pathOrDoc)}`;
}

/** Return canonical path if the requested path should 301, else null. */
export function canonicalRedirectTarget(requestedPath: string): string | null {
  const raw = String(requestedPath || '').trim();
  if (!raw) return null;

  const asRequested = raw.startsWith('/') ? raw : `/${raw}`;
  const normalized = normalizeLegalPath(raw);

  if (LEGACY_SHORT_REDIRECTS[normalized]) {
    return canonicalLegalPath(LEGACY_SHORT_REDIRECTS[normalized]);
  }

  const shortProbe = asRequested.toLowerCase().replace(/\/{2,}/g, '/').replace(/\/+$/, '') || '/';
  if (LEGACY_SHORT_REDIRECTS[shortProbe]) {
    return canonicalLegalPath(LEGACY_SHORT_REDIRECTS[shortProbe]);
  }

  if (!normalized.startsWith('/legal/')) return null;

  return asRequested !== normalized ? normalized : null;
}

export function legacyRedirectTarget(shortPath: string): string | null {
  const normalized = normalizeLegalPath(shortPath);
  const slug = LEGACY_SHORT_REDIRECTS[normalized];
  return slug ? canonicalLegalPath(slug) : null;
}

export function legalAdminOriginFallback(): string {
  return LOCAL_BACKEND_URL.replace(/\/api\/?$/, '') || 'http://127.0.0.1:8000';
}
